import { Entity } from '@backstage/catalog-model';
import { AIProvisioningServiceApi, AIResourcesDeletionServiceApi } from '@runway/devkit';
import {
  AIFoundryProjectNotFoundError,
  AzureManagementServiceApi,
  extractVerticalFromAIConfig,
} from '@runway/devkit-common';
import { CatalogProjectsServiceApi } from '../../services/catalogProjects.service';

type TriggerAIInfrastructureDeletionParams = {
  entity: Entity;
  clusterName: string;
  userEmployeeId: string;
  deletionService: AIResourcesDeletionServiceApi;
  provisioningService: AIProvisioningServiceApi;
  catalogProjectsService: CatalogProjectsServiceApi;
  azureManagementService: AzureManagementServiceApi;
};

type TriggerAIInfrastructureDeletionResult = {
  /** True when one or more AI Foundry projects were already gone (HTTP 404) prior to deletion. */
  foundryProjectsNotFound: boolean;
};

type EntityWithAIInfo = Entity & {
  spec?: {
    aiInfo?: {
      aiCategory?: string;
    };
  };
};

export const triggerAIInfrastructureDeletion = async ({
  entity,
  clusterName,
  userEmployeeId,
  deletionService,
  provisioningService,
  catalogProjectsService,
  azureManagementService,
}: TriggerAIInfrastructureDeletionParams): Promise<TriggerAIInfrastructureDeletionResult> => {
  let deploymentEnvironment: 'prod' | 'nonprod' | undefined;
  if (clusterName.includes('-p-')) {
    deploymentEnvironment = 'prod';
  } else if (clusterName.includes('-np-') || clusterName.includes('-lab-')) {
    deploymentEnvironment = 'nonprod';
  }

  if (!deploymentEnvironment) {
    throw new Error(`Unable to determine deployment environment from cluster name '${clusterName}'.`);
  }

  const entityName = entity.metadata.name;
  const aiCategory = (entity as EntityWithAIInfo).spec?.aiInfo?.aiCategory;

  if (!aiCategory) {
    throw new Error('AI category is missing from entity spec. Unable to delete associated AI infrastructure.');
  }

  const vaultNamespace = await deletionService.fetchVaultNamespace(entityName);
  if (!vaultNamespace) {
    throw new Error('VAULT_NAMESPACE could not be resolved from the repository.');
  }

  await deletionService.deleteAzureAPIMSecrets({
    apiManagementEnvironment: deploymentEnvironment,
    productId: `${entityName}-product`,
    vaultNamespace,
    employeeID: userEmployeeId,
  });

  const appShortname = await provisioningService.resolveAppShortname(catalogProjectsService, entity, entityName);

  await deletionService.deleteHiddenLayerPolicyControl({
    project_name: entityName,
    shortname: appShortname,
  });

  const aiConfig = await provisioningService.fetchAIConfig(appShortname, aiCategory);
  const vertical = extractVerticalFromAIConfig(aiConfig);

  if (!vertical) {
    throw new Error('Could not determine vertical from AI config.');
  }

  const foundries = await azureManagementService.getAIFoundries({ env: deploymentEnvironment, vertical });
  const firstFoundryResult = foundries.results[0];

  if (!firstFoundryResult || firstFoundryResult.foundryNames.length < 2) {
    throw new Error(
      'No AI Foundries found for this entity or insufficient foundry names. Cannot proceed with deletion.',
    );
  }

  const deletionOutcomes = await Promise.all(
    firstFoundryResult.foundryNames.slice(0, 2).map(async (foundryResourceName: string) => {
      try {
        await azureManagementService.deleteAIResources({
          tenant: 'p',
          subscriptionId: firstFoundryResult.subscriptionId,
          resourceGroupName: firstFoundryResult.resourceGroupName,
          foundryResourceName,
          projectName: entityName,
        });
        return { notFound: false };
      } catch (error: unknown) {
        if (error instanceof AIFoundryProjectNotFoundError) {
          return { notFound: true };
        }
        throw error;
      }
    }),
  );

  return { foundryProjectsNotFound: deletionOutcomes.some(outcome => outcome.notFound) };
};
